/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { ActionButton, Button, Flex, Heading } from '@adobe/react-spectrum'
import { useAtom } from 'jotai'

import RotateCCWIcon from '@spectrum-icons/workflow/RotateCCW'
import RotateCWIcon from '@spectrum-icons/workflow/RotateCW'
import AddIcon from '@spectrum-icons/workflow/Add'
import RemoveIcon from '@spectrum-icons/workflow/Remove'

import { FormItem } from '../components/FormItem'
import { Stepper } from '../components/Stepper'
import { DefaultViewerConfig, viewerAtom } from '../config'

const Rotate45 = ({
  value,
  onChange,
}: {
  value: number
  onChange: (value: number) => void
}) => {
  function handleOnChange(newValue: number) {
    //@ts-ignore
    onChange(value + newValue)
  }

  return (
    <Flex justifyContent="space-around">
      <ActionButton
        isQuiet
        onPress={() => handleOnChange(-45)}
        aria-label="rotation -45°"
      >
        <RotateCCWIcon aria-label="rotation -45° icon" />
      </ActionButton>
      <ActionButton
        isQuiet
        onPress={() => handleOnChange(45)}
        aria-label="rotation +45°"
      >
        <RotateCWIcon aria-label="rotation +45° icon" />
      </ActionButton>
    </Flex>
  )
}

export function rad2deg(rad: number) {
  return (rad * 180) / Math.PI
}

export function deg2rad(deg: number) {
  return (deg * Math.PI) / 180
}

function ModelPanel() {
  const [atom, setAtom] = useAtom(viewerAtom)

  const handleNodeTranslationChange = (axis: string, value: number) => {
    const translation = atom.node.translation
    // @ts-ignore
    translation[axis] = value
    setAtom({ ...atom, node: { ...atom.node, translation } })
  }

  const handleModelTranslationChange = (axis: string, value: number) => {
    const translation = atom.model.translation
    // @ts-ignore
    translation[axis] = value
    setAtom({ ...atom, model: { ...atom.model, translation } })
  }

  const handleNodeRotationChange = (axis: string, value: number) => {
    const rotation = atom.node.rotation
    // @ts-ignore
    rotation[axis] = deg2rad(value)
    setAtom({ ...atom, node: { ...atom.node, rotation } })
  }

  const handleModelRotationChange = (axis: string, value: number) => {
    const rotation = atom.model.rotation
    // @ts-ignore
    rotation[axis] = deg2rad(value)
    setAtom({ ...atom, model: { ...atom.model, rotation } })
  }

  const handleNodeReset = () => {
    setAtom((prev) => ({
      ...prev,
      // did not want to work with DefaultViewerConfig directly
      node: {
        ...prev.node,
        translation: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scaling: { x: 1, y: 1, z: 1 },
      },
    }))
  }
  const handleReset = () => {
    setAtom((prev) => ({
      ...prev,
      // did not want to work with DefaultViewerConfig directly
      model: {
        ...prev.model,
        translation: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scaling: { x: 1, y: 1, z: 1 },
      },
    }))
  }

  const handleNodeScalingChange = (axis: string, value: number) => {
    const scaling = atom.node.scaling
    // @ts-ignore
    scaling[axis] = value
    setAtom({ ...atom, node: { ...atom.node, scaling } })
  }
  const handleModelScalingChange = (axis: string, value: number) => {
    const scaling = atom.model.scaling
    // @ts-ignore
    scaling[axis] = value
    setAtom({ ...atom, model: { ...atom.model, scaling } })
  }
  const handleNodeScalingChangeUnified = (value: number) => {
    const scaling = atom.node.scaling
    scaling.x += value
    scaling.y += value
    scaling.z += value
    setAtom({ ...atom, node: { ...atom.node, scaling } })
  }
  const handleModelScalingChangeUnified = (value: number) => {
    const scaling = atom.model.scaling
    scaling.x += value
    scaling.y += value
    scaling.z += value
    setAtom({ ...atom, model: { ...atom.model, scaling } })
  }

  return (
    <>
      <Heading level={1}>
        <Flex justifyContent="space-between" alignItems="center">
          <span>Node</span>
          <Button variant="primary" onPress={handleNodeReset}>
            Reset
          </Button>
        </Flex>
      </Heading>

      {/*----------------------
      ----- Node Position
      ----------------------*/}
      <Heading level={2} marginY={0}>
        Position
      </Heading>
      <Flex gap="size-100">
        <FormItem>
          <Stepper
            label="X"
            defaultValue={DefaultViewerConfig.node.translation.x}
            value={atom.node.translation.x}
            onChange={handleNodeTranslationChange}
          />
        </FormItem>
        <FormItem>
          <Stepper
            label="Y"
            defaultValue={DefaultViewerConfig.node.translation.y}
            value={atom.node.translation.y}
            onChange={handleNodeTranslationChange}
          />
        </FormItem>
        <FormItem>
          <Stepper
            label="Z"
            defaultValue={DefaultViewerConfig.node.translation.z}
            value={atom.node.translation.z}
            onChange={handleNodeTranslationChange}
          />
        </FormItem>
      </Flex>

      {/*----------------------
      ----- Node Rotation
      ----------------------*/}
      <Heading level={2} marginY={0}>
        Rotation
      </Heading>
      <Flex gap="size-100">
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="X"
              defaultValue={rad2deg(DefaultViewerConfig.node.rotation.x)}
              value={rad2deg(atom.node.rotation.x)}
              onChange={handleNodeRotationChange}
            />
            <Rotate45
              value={rad2deg(atom.node.rotation.x)}
              onChange={(value: number) => handleNodeRotationChange('x', value)}
            />
          </Flex>
        </FormItem>
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="Y"
              defaultValue={rad2deg(DefaultViewerConfig.node.rotation.y)}
              value={rad2deg(atom.node.rotation.y)}
              onChange={handleNodeRotationChange}
            />
            <Rotate45
              value={rad2deg(atom.node.rotation.y)}
              onChange={(value: number) => handleNodeRotationChange('y', value)}
            />
          </Flex>
        </FormItem>
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="Z"
              defaultValue={rad2deg(DefaultViewerConfig.node.rotation.z)}
              value={rad2deg(atom.node.rotation.z)}
              onChange={handleNodeRotationChange}
            />
            <Rotate45
              value={rad2deg(atom.node.rotation.z)}
              onChange={(value: number) => handleNodeRotationChange('z', value)}
            />
          </Flex>
        </FormItem>
      </Flex>

      {/*----------------------
      ----- Node Scaling
      ----------------------*/}
      <Heading level={2} marginY={0}>
        <Flex gap="size-100" alignItems="center">
          Scaling
          <ActionButton
            isQuiet
            onPress={() => handleNodeScalingChangeUnified(0.1)}
            aria-label="increase scaling"
          >
            <AddIcon />
          </ActionButton>
          <ActionButton
            isQuiet
            onPress={() => handleNodeScalingChangeUnified(-0.1)}
            aria-label="decrease scaling"
          >
            <RemoveIcon />
          </ActionButton>
        </Flex>
      </Heading>
      <Flex gap="size-100">
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="X"
              minValue={0.1}
              defaultValue={DefaultViewerConfig.node.scaling.x}
              value={atom.node.scaling.x}
              onChange={handleNodeScalingChange}
            />
          </Flex>
        </FormItem>
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="Y"
              minValue={0.1}
              defaultValue={DefaultViewerConfig.node.scaling.y}
              value={atom.node.scaling.y}
              onChange={handleNodeScalingChange}
            />
          </Flex>
        </FormItem>
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="Z"
              minValue={0.1}
              defaultValue={DefaultViewerConfig.node.scaling.z}
              value={atom.node.scaling.z}
              onChange={handleNodeScalingChange}
            />
          </Flex>
        </FormItem>
      </Flex>

      <Heading level={1}>
        <Flex justifyContent="space-between" alignItems="center">
          <span>Model</span>
          <Button variant="primary" onPress={handleReset}>
            Reset
          </Button>
        </Flex>
      </Heading>

      {/*----------------------
      ----- Model Position
      ----------------------*/}
      <Heading level={2} marginY={0}>
        Position
      </Heading>
      <Flex gap="size-100">
        <FormItem>
          <Stepper
            label="X"
            defaultValue={DefaultViewerConfig.model.translation.x}
            value={atom.model.translation.x}
            onChange={handleModelTranslationChange}
          />
        </FormItem>
        <FormItem>
          <Stepper
            label="Y"
            defaultValue={DefaultViewerConfig.model.translation.y}
            value={atom.model.translation.y}
            onChange={handleModelTranslationChange}
          />
        </FormItem>
        <FormItem>
          <Stepper
            label="Z"
            defaultValue={DefaultViewerConfig.model.translation.z}
            value={atom.model.translation.z}
            onChange={handleModelTranslationChange}
          />
        </FormItem>
      </Flex>

      {/*----------------------
      ----- Model Rotation
      ----------------------*/}
      <Heading level={2} marginY={0}>
        Rotation
      </Heading>
      <Flex gap="size-100">
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.01}
              label="X"
              defaultValue={rad2deg(DefaultViewerConfig.model.rotation.x)}
              value={rad2deg(atom.model.rotation.x)}
              onChange={handleModelRotationChange}
            />
            <Rotate45
              value={rad2deg(atom.model.rotation.x)}
              onChange={(value: number) =>
                handleModelRotationChange('x', value)
              }
            />
          </Flex>
        </FormItem>
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="Y"
              defaultValue={rad2deg(DefaultViewerConfig.model.rotation.y)}
              value={rad2deg(atom.model.rotation.y)}
              onChange={handleModelRotationChange}
            />
            <Rotate45
              value={rad2deg(atom.model.rotation.y)}
              onChange={(value: number) =>
                handleModelRotationChange('y', value)
              }
            />
          </Flex>
        </FormItem>
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="Z"
              defaultValue={rad2deg(DefaultViewerConfig.model.rotation.z)}
              value={rad2deg(atom.model.rotation.z)}
              onChange={handleModelRotationChange}
            />
            <Rotate45
              value={rad2deg(atom.model.rotation.z)}
              onChange={(value: number) =>
                handleModelRotationChange('z', value)
              }
            />
          </Flex>
        </FormItem>
      </Flex>

      {/*----------------------
      ----- Model Scaling
      ----------------------*/}
      <Heading level={2} marginY={0}>
        <Flex gap="size-100" alignItems="center">
          Scaling
          <ActionButton
            isQuiet
            onPress={() => handleModelScalingChangeUnified(0.1)}
            aria-label="increase scaling"
          >
            <AddIcon />
          </ActionButton>
          <ActionButton
            isQuiet
            onPress={() => handleModelScalingChangeUnified(-0.1)}
            aria-label="decrease scaling"
          >
            <RemoveIcon />
          </ActionButton>
        </Flex>
      </Heading>
      <Flex gap="size-100">
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="X"
              minValue={0.1}
              defaultValue={DefaultViewerConfig.model.scaling.x}
              value={atom.model.scaling.x}
              onChange={handleModelScalingChange}
            />
          </Flex>
        </FormItem>
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="Y"
              minValue={0.1}
              defaultValue={DefaultViewerConfig.model.scaling.y}
              value={atom.model.scaling.y}
              onChange={handleModelScalingChange}
            />
          </Flex>
        </FormItem>
        <FormItem>
          <Flex gap="size-100" direction="column">
            <Stepper
              step={0.1}
              label="Z"
              minValue={0.1}
              defaultValue={DefaultViewerConfig.model.scaling.z}
              value={atom.model.scaling.z}
              onChange={handleModelScalingChange}
            />
          </Flex>
        </FormItem>
      </Flex>
    </>
  )
}

export { ModelPanel }
